import React, { useState } from 'react';
import { Typography, Button, Modal, Input } from 'iglooform';
import { useIntl } from 'react-intl';
import { images } from '@/assets/images';
import { AlertFilled } from 'iglooicon';
import { sendEmailOtp, verifyEmailOtp } from '@/service';
import './index.less';
import _ from 'lodash';
import { emailReg } from '@/utils/regexp';

const EmailVerification = ({
  email,
  verifiSuccess,
  setVerifiSuccess,
  platformKey,
}: {
  email: string;
  verifiSuccess: boolean;
  setVerifiSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  platformKey: string;
}) => {
  const { formatMessage } = useIntl();
  const [countdown, setCountdown] = useState<number>(0);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [sendOtpLoading, setSendOtpLoading] = useState<boolean>(false);
  const [verifyLoading, setVerifyLoading] = useState<boolean>(false);
  const [inputHasError, setInputHasError] = useState<boolean>(false);

  const emailFormatIsCorrect = !!email && emailReg.test(email.trim());
  const canVerify = !!verificationCode;

  const closeAndInit = () => {
    setModalVisible(false);
    setVerificationCode('');
    setSendOtpLoading(false);
    setVerifyLoading(false);
    setInputHasError(false);
  };

  const onCancel = () => {
    closeAndInit();
  };

  const onOk = _.throttle(
    () => {
      setVerifyLoading(true);
      verifyEmailOtp({
        email,
        code: verificationCode,
      })
        .then((res) => {
          if (res?.valid === true) {
            setVerifiSuccess(true);
            closeAndInit();
          } else if (res?.valid === false) {
            setInputHasError(true);
          } else {
            // 返回其他数据对象则不处理
          }
        })
        .finally(() => {
          setVerifyLoading(false);
        });
    },
    1000,
    {
      leading: true,
      trailing: false,
    },
  );

  const getOtp = () => {
    setSendOtpLoading(true);
    sendEmailOtp({
      email,
      platform: platformKey,
    })
      .then((res) => {
        if (res?.status === 'ok') {
          // 弹框没打开的话就打开
          !modalVisible && setModalVisible(true);
          // 开启倒计时
          setCountdown(60);
          const timer = setInterval(() => {
            setCountdown((prevTime) => {
              if (prevTime - 1 === 0) {
                window.clearInterval(timer);
              }
              return prevTime - 1;
            });
          }, 1000);
        }
      })
      .finally(() => {
        setSendOtpLoading(false);
      });
  };

  const getOtpThrottle = _.throttle(getOtp, 1000, {
    leading: true,
    trailing: false,
  });

  const resendBtn = (
    <Button
      type="link"
      style={{ color: '#5858FF' }}
      onClick={getOtpThrottle}
      loading={sendOtpLoading}
    >
      {formatMessage({
        id: 'Resend',
        defaultMessage: 'Resend',
      })}
    </Button>
  );

  const resendCountdownText = (
    <Typography level="body1" style={{ color: '#BDBDBD' }}>
      {formatMessage(
        {
          id: 'Resend in {countdown}(s)',
          defaultMessage: 'Resend in {countdown}(s)',
        },
        {
          countdown: countdown,
        },
      )}
    </Typography>
  );

  const countdownBtn = (
    <div className="countdownBtn">
      {formatMessage(
        {
          id: '{countdown}(s)',
          defaultMessage: '{countdown}(s)',
        },
        {
          countdown,
        },
      )}
    </div>
  );

  const verifyBtn = (
    <Button
      type="link"
      style={{ color: '#5858FF' }}
      loading={sendOtpLoading}
      disabled={!emailFormatIsCorrect}
      onClick={getOtpThrottle}
    >
      {formatMessage({
        id: 'Verify',
        defaultMessage: 'Verify',
      })}
    </Button>
  );

  const beforeVerifiSuccessBtn = countdown !== 0 ? countdownBtn : verifyBtn;

  const onClickChange = () => {
    setVerifiSuccess(false);
  };

  const changeBtn = (
    <Button type="link" style={{ color: '#5858FF' }} onClick={onClickChange}>
      {formatMessage({
        id: 'Change',
        defaultMessage: 'Change',
      })}
    </Button>
  );

  return (
    <>
      {verifiSuccess ? changeBtn : beforeVerifiSuccessBtn}
      {/* 验证弹框 */}
      <Modal
        visible={modalVisible}
        className="verifyEmailModal"
        title={formatMessage({
          id: 'Verify',
          defaultMessage: 'Verify',
        })}
        okText={formatMessage({
          id: 'Verify',
          defaultMessage: 'Verify',
        })}
        okButtonProps={{ disabled: !canVerify, loading: verifyLoading }}
        cancelButtonProps={{
          style: {
            display: 'none',
          },
        }}
        onOk={onOk}
        onCancel={onCancel}
        destroyOnClose={true}
        maskClosable={false}
      >
        <div className="verifyEmailContent">
          <div className="emailImg">
            <img src={images.email_verification} alt="" />
          </div>
          <Typography level="h3a" className="title">
            {formatMessage({
              id: 'Email Verification',
              defaultMessage: 'Email Verification',
            })}
          </Typography>
          <Typography level="body1" className="subtitle">
            {formatMessage(
              {
                id: 'Enter the code sent to {email}',
                defaultMessage: 'Enter the code sent to {email}',
              },
              {
                email: email,
              },
            )}
          </Typography>
          <div className="inputWrap">
            <Input
              defaultValue={verificationCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setInputHasError(false);
                setVerificationCode(e?.target?.value);
              }}
              suffix={
                inputHasError ? (
                  <AlertFilled className="errorSuffix" />
                ) : undefined
              }
              status={inputHasError ? 'error' : undefined}
            />
            {inputHasError && (
              <span className="errorTip">
                {formatMessage({
                  id: 'Invalid OTP entered',
                  defaultMessage: 'Invalid OTP entered',
                })}
              </span>
            )}
          </div>
          <Typography level="body1">
            {formatMessage({
              id: "Don't receive OTP?",
              defaultMessage: "Don't receive OTP?",
            })}{' '}
            {countdown ? resendCountdownText : resendBtn}
          </Typography>
        </div>
      </Modal>
    </>
  );
};

export default EmailVerification;
